// Lightweight first-party analytics tracker.
// - Persists visitor_id in localStorage, session_id in sessionStorage.
// - Deduplicates rapid repeat pageviews on the same path.
// - Sends an article_view with reading duration on unload.
import { api } from "@/lib/api";

const VISITOR_KEY = "popu_visitor_id";
const SESSION_KEY = "popu_session_id";

function uuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "v-" + Math.random().toString(36).slice(2) + "-" + Date.now().toString(36);
}

function getVisitorId() {
  try {
    let v = localStorage.getItem(VISITOR_KEY);
    if (!v) { v = uuid(); localStorage.setItem(VISITOR_KEY, v); }
    return v;
  } catch (_) { return null; }
}

function getSessionId() {
  try {
    let s = sessionStorage.getItem(SESSION_KEY);
    if (!s) { s = uuid(); sessionStorage.setItem(SESSION_KEY, s); }
    return s;
  } catch (_) { return null; }
}

function detectDevice() {
  if (typeof navigator === "undefined") return "desktop";
  const ua = navigator.userAgent.toLowerCase();
  if (/iphone|android|mobile/.test(ua)) return "mobile";
  if (/ipad|tablet/.test(ua)) return "tablet";
  return "desktop";
}

let _lastPath = null;
let _lastTime = 0;
let _currentArticleStart = null;
let _currentArticleSlug = null;

export function trackPageview({ path, articleSlug, articleId } = {}) {
  const p = path || (typeof window !== "undefined" ? window.location.pathname : "");
  const now = Date.now();
  // Deduplicate same path within 2s (double-firing on fast nav)
  if (_lastPath === p && now - _lastTime < 2000) return;
  _lastPath = p; _lastTime = now;

  // If leaving an article, flush its duration first
  flushArticleDuration();

  const payload = {
    type: articleSlug ? "article_view" : "pageview",
    path: p,
    article_slug: articleSlug || null,
    article_id: articleId || null,
    visitor_id: getVisitorId(),
    session_id: getSessionId(),
    referrer: (document.referrer || "").slice(0, 300),
    screen: `${window.screen?.width}x${window.screen?.height}`,
    device: detectDevice(),
  };
  api.post("/analytics/track", payload).catch(() => { /* silent */ });

  if (articleSlug) {
    _currentArticleStart = now;
    _currentArticleSlug = articleSlug;
  } else {
    _currentArticleStart = null;
    _currentArticleSlug = null;
  }
}

export function flushArticleDuration() {
  if (!_currentArticleStart || !_currentArticleSlug) return;
  const dur = Date.now() - _currentArticleStart;
  if (dur < 2000) return; // ignore bounces <2s
  const payload = {
    type: "article_view",
    article_slug: _currentArticleSlug,
    duration_ms: dur,
    visitor_id: getVisitorId(),
    session_id: getSessionId(),
    device: detectDevice(),
    data: { kind: "duration_flush" },
  };
  try {
    const url = (process.env.REACT_APP_BACKEND_URL || "") + "/api/analytics/track";
    const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
    if (navigator.sendBeacon && navigator.sendBeacon(url, blob)) {
      _currentArticleStart = null;
      return;
    }
  } catch (_) { /* fall through */ }
  api.post("/analytics/track", payload).catch(() => { /* silent */ });
  _currentArticleStart = null;
}

export function trackAdImpression(campaignId) {
  if (!campaignId) return;
  api.post("/analytics/track", {
    type: "ad_impression",
    campaign_id: campaignId,
    visitor_id: getVisitorId(),
    session_id: getSessionId(),
    device: detectDevice(),
  }).catch(() => {});
}

export function trackAdClick(campaignId, destination) {
  if (!campaignId) return;
  api.post("/analytics/track", {
    type: "ad_click",
    campaign_id: campaignId,
    visitor_id: getVisitorId(),
    session_id: getSessionId(),
    device: detectDevice(),
    data: { destination },
  }).catch(() => {});
}

if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", flushArticleDuration);
  window.addEventListener("pagehide", flushArticleDuration);
}
